import { ApiCertificateItem, ApiCountry, ApiCustomField, ApiEnumGroup, ApiFunctionFull, ApiOrganizationSummeryOverview, ApiRole, type ApiImageWrapper, type ApiItemTypes, type ApiOrganization, type ApiOrganizationYield, type ApiRealSimpleOrganization, type GenericHttpResult } from '@agunity/api-v4';
import { apiService } from '../../api/apiService';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';

export interface OrgRoleState { 
  functionId?: number; 
  id: number; 
  name: string 
}

export interface OrgsMgtState {
  orgList: ApiRealSimpleOrganization[] | null;
  searchOrgId: string | null;
  countryOptions: ApiCountry[] | [];
  functionStructureList: ApiFunctionFull[];
  isDirty_Org_Details: boolean;
  isOpenViewAvatar: boolean;
  isOpenUnsavedModal: boolean;
  org_Redirect_URL: string;
  tagList: ApiCustomField[];
  enumList: ApiEnumGroup[];
  selectedOrgName: string;
  orgRoles: ApiRole[];
  selectedRole?: OrgRoleState;
  isToastOpen_navFromAddOrganization: boolean;
}

const initialState: OrgsMgtState = {
  orgList: null,
  searchOrgId: null,
  countryOptions: [],
  functionStructureList: [],
  isDirty_Org_Details: false,
  isOpenViewAvatar: false,
  isOpenUnsavedModal: false,
  org_Redirect_URL: '',
  tagList: [],
  enumList: [],
  /** For view org details page */
  selectedOrgName: '',
  orgRoles: [],
  isToastOpen_navFromAddOrganization: false
};

export const orgsSlice = createSlice({
  name: 'orgs',
  initialState,
  reducers: {
    store: (
      state: OrgsMgtState,
      action: PayloadAction<ApiRealSimpleOrganization[]>
    ) => {
      // Copy each property from the payload to the state
      state.orgList = action.payload;
    },
    clear: (
      state: OrgsMgtState,
    ) => {
      // Set each property of the state back to its initial value
      Object.assign(state, initialState);
    },
    setSearchOrgId: (state: OrgsMgtState, action: PayloadAction<string>) => {
      state.searchOrgId = action.payload;
    },
    setOrgCountryOptions: (
      state: OrgsMgtState,
      action: PayloadAction<ApiCountry[] | []>
    ) => {
      state.countryOptions = action.payload;
    },
    setFunctionStructureList: (
      state: OrgsMgtState,
      action: PayloadAction<ApiOrganization[] | []>
    ) => {
      state.functionStructureList = action.payload;
    },
    setIsDirty_Org_Details: (
      state: OrgsMgtState,
      action: PayloadAction<boolean>
    ) => {
      state.isDirty_Org_Details = action.payload;
    },
    setIsOpenViewAvatar: (
      state: OrgsMgtState,
      action: PayloadAction<boolean>
    ) => {
      state.isOpenViewAvatar = action.payload;
    },
    setIsOpenUnsavedModal: (
      state: OrgsMgtState,
      action: PayloadAction<boolean>
    ) => {
      state.isOpenUnsavedModal = action.payload;
    },
    setOrg_Redirect_URL: (
      state: OrgsMgtState,
      action: PayloadAction<string>
    ) => {
      state.org_Redirect_URL = action.payload;
    },
    setOrg_TagList: (
      state: OrgsMgtState,
      action: PayloadAction<ApiCustomField[]>
    ) => {
      state.tagList = action.payload;
    },
    setOrg_EnumList: (
      state: OrgsMgtState,
      action: PayloadAction<ApiEnumGroup[]>
    ) => {
      state.enumList = action.payload;
    },
    setSelectedOrgName: (
      state: OrgsMgtState,
      action: PayloadAction<string>
    ) => {
      state.selectedOrgName = action.payload;
    },
    setOrgRoles: (
      state: OrgsMgtState,
      action: PayloadAction<ApiRole[]>
    ) => {
      state.orgRoles = action.payload;
    },
    setSelectedRole: (
      state: OrgsMgtState,
      action: PayloadAction<OrgRoleState>
    ) => {
      state.selectedRole = action.payload;
    },
    setIsToastOpen_navFromAddOrganization: (
      state: OrgsMgtState,
      action: PayloadAction<boolean>
    ) => {
      state.isToastOpen_navFromAddOrganization = action.payload;
    }
  }
});

export const { 
  store,
  clear,
  setSearchOrgId,
  setOrgCountryOptions,
  setFunctionStructureList,
  setIsDirty_Org_Details,
  setIsOpenViewAvatar,
  setIsOpenUnsavedModal,
  setOrg_Redirect_URL,
  setOrg_TagList,
  setSelectedOrgName,
  setOrgRoles,
  setSelectedRole,
  setIsToastOpen_navFromAddOrganization,
  setOrg_EnumList
} = orgsSlice.actions;

export const selectOrgList = (state: RootState) => state.orgs.orgList;
export const selectSearchOrgId = (state: RootState) => state.orgs.searchOrgId;
export const selectIsOpenViewAvatar = (state: RootState) => state.orgs.isOpenViewAvatar;

export const fetchChildOrgs =
  () =>
    async (
      dispatch: Function,
    ): Promise<ApiRealSimpleOrganization[]> => {
      const orgs: ApiRealSimpleOrganization[] = (await apiService.organizationV20ChildOrganizationsList())?.data;
      if (orgs) {
        dispatch(store(orgs));
      }
      return orgs;
    };

export const getOrganization =
  (orgId: number) =>
    async () => {
      const response = await apiService.organizationV20ItemDetail(orgId);
      return response;
    };

export const getOrganizationType =
  (functionId: number) =>
    async () => {
      const response = await apiService.uiV10AssignableDetail(functionId);
      return response;
    };

export const getCustomField =
  (itemTypeId: number) =>
    async () => {
      const response = await apiService.customFieldV10ListDetail(itemTypeId);
      return response;
    };

export const getTreeExplain =
  (orgId: number) =>
    async () => {
      const response = await apiService.uiV10SystemSummaryDetail(orgId);
      return response;
    };

export const getSummary =
  () =>
    async () => {
      const response = await apiService.uiV10SystemSummaryList();
      return response;
    };

export const getOverview =
  () =>
    async () => {
      const response = await apiService.uiV10OverviewList();
      return response;
    };

export const getCerificateOverview = () => {
  return async (): Promise<GenericHttpResult<ApiCertificateItem[]>> => {
    const response = await apiService.certificateV10OverviewList();
    return response;
  };
};

export const getImage =
  (typeId: ApiItemTypes, id: number) =>
    async () => {
      const response: GenericHttpResult<string> = await apiService.blobV30Base64Detail(typeId, id);
      return response;
    };

export const getCooperativeSummary = () => {
  return async (): Promise<GenericHttpResult<ApiOrganizationSummeryOverview>> => {
    const response = await apiService.uiV10OrgOverviewList();
    return response;
  };
};

export const saveImage =
  (data: ApiImageWrapper) =>
    async () => {
      const response = await apiService.imageV20ItemCreate(data);
      return response;
    };

export const updateOrgStatus =
  (orgId: number, isActive: boolean) =>
    async () => {
      const response = await apiService.organizationV10ItemActiveFlagPartialUpdate(orgId, isActive);
      return response;
    };

export const saveOrg =
  (orgId: number, data: ApiOrganization) =>
    async () => {
      const response = await apiService.organizationV20ItemPartialUpdate(orgId, data);
      return response;
    };

export const saveNewOrg =
  (data: ApiOrganization) =>
    async () => {
      const response = await apiService.organizationV20ItemCreate(data);
      return response;
    };

export const orgTypeList =
  (orgId: number) =>
    async () => {
      const response = await apiService.functionV10AssignableFromOrgDetail(orgId);
      return response;
    };

export const getStructure =
  () =>
    async () => {
      try {
        const response = await apiService.functionV10StructureList();
        return response;
      }catch (e) {
        console.error(e);
      }
    };

export const getOrgDirectChildren =
  (orgId: number) =>
    async () => {
      const response = await apiService.organizationV10DirectChildrenDetail(orgId);
      return response;
    };

export const getOrgYields =
  (orgId: number) =>
    async () => {
      const response = await apiService.organizationV10YieldsDetail(orgId);
      return response;
    };

export const editOrgYields =
  (orgId: number, data: ApiOrganizationYield[]) =>
    async () => {
      const response = await apiService.organizationV10YieldsCreate(orgId, data);
      return response;
    };

export const getAssetType =
  () =>
    async () => {
      const response = await apiService.assetTypeV20ListList();
      return response;
    };

export const getAssetTypeDefaultUnit =
  () =>
    async () => {
      const response = await apiService.organizationV10AssetTypesDefaultYieldUnitsList();
      return response;
    };

export const getListOfOrgAssetType =
  (orgId: number) =>
    async () => {
      const response = await apiService.organizationV10AssetTypesParentDetail(orgId);
      return response;
    };

export const getListOfOrgAssetTypeCompact =
  () =>
    async () => {
      const response: GenericHttpResult<number[]> = await apiService.licenseV10AssetTypesCompactList();
      return response;
    };

export const getSparseTreePath =
  (orgId: number) =>
    async () => {
      const response = await apiService.organizationV10SparseTreePathDetail(orgId);
      return response;
    };

export const updatePartialOrgContract =
  (id: number, data: any) =>
    async () => {
      const response = await apiService.contractV10ItemPartialUpdate(id, data);
      return response;
    };

export const deleteOrgContract =
  (id: number) =>
    async () => {
      const response = await apiService.contractV10ItemDelete(id);
      return response;
    };

export const getContractOrgContractDetails =
  (id: number) =>
    async () => {
      const response = await apiService.contractV10ItemDetail(id);
      return response;
    };

export const getOrgSummary =
  (orgId: number) =>
    async () => {
      const response = await apiService.uiV10OrgSummaryDetail(orgId);
      return response;
    };

export default orgsSlice.reducer;